import type { ReactNode } from "react"
import { useEffect } from "react"
import { clearAuth, getTokenAvailable, suspendAuth } from "./slice"
import { getRefreshToken } from "./refreshTokenStorage"
import { withRefreshLock } from "./refreshLock"
import { isRefreshTokenRejected } from "./refreshFailure"
import { useTokenRefreshMutation } from "@/base/baseApi"
import { useDispatch, useSelector } from "@/base/store"

type Props = {
  children: ReactNode
}

export default function AuthWrapper({ children }: Props) {
  const isTokenAvailable = useSelector(getTokenAvailable)
  const [tokenRefresh] = useTokenRefreshMutation()
  const dispatch = useDispatch()

  useEffect(() => {
    if (isTokenAvailable) {
      return
    }

    // Under the same lock as baseApi's 401 refresh: the token rotates, so two exchanges of the
    // same value end the session. Read it inside the lock only: a tab that waited here must use
    // whatever the winner wrote.
    void withRefreshLock(async () => {
      const refreshToken = getRefreshToken()
      if (!refreshToken) {
        dispatch(clearAuth())

        return
      }

      try {
        await tokenRefresh(refreshToken).unwrap()

        return
      } catch (error) {
        dispatch(
          isRefreshTokenRejected(error)
            ? clearAuth(refreshToken)
            : suspendAuth(),
        )
      }
    })
  }, [isTokenAvailable, dispatch, tokenRefresh])

  return children
}
